import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import AiChatMutationDto from "../../dto/Ai/AiChatMutationDto";
import { OFFICE_TYPES } from "../../dto/Company/CompanySettingsDto";
import AiAnswerModel from "../../viewModels/Ai/AiAnswerModel";
import AiChatListItemModel from "../../viewModels/Ai/AiChatListItemModel";
import AiChatMessageModel from "../../viewModels/Ai/AiChatMessageModel";
import { AdminPanelCompanySettingsModel } from "../../viewModels/Settings/AdminPanelCompanySettingsModel";
import {
  AgentConversationContextOptionsDto,
  AgentConversationContextRequestDto,
} from "../../api/apiServices/ai/controllers/agent/agent.requestsDto";

const sortAiChats = (chats: Array<AiChatListItemModel>) =>
  chats.sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) {
      return a.pinned ? -1 : 1;
    }

    const aDate = a.updatedAt || a.lastMessageAt || a.createdAt;
    const bDate = b.updatedAt || b.lastMessageAt || b.createdAt;

    return (bDate?.valueOf() || 0) - (aDate?.valueOf() || 0);
  });

const createLocalUserMessage = (text: string): AiChatMessageModel => ({
  id: -Date.now(),
  role: "USER",
  text,
  ts: dayjs(),
});

const createLocalAssistantMessage = (answer: AiAnswerModel): AiChatMessageModel => ({
  id: answer.assistantMessageId || -Date.now(),
  role: "ASSISTANT",
  text: answer.answer,
  ts: dayjs(),
  intent: answer.intent,
  finalStatus: answer.finalStatus,
  errorCode: answer.errorCode,
  attemptGroupId: answer.attemptGroupId,
  toolCallsCount: answer.toolCallsCount,
  latencyMs: answer.latencyMs,
  entityLinks: answer.entityLinks,
  warnings: answer.warnings,
  suggestedActions: answer.suggestedActions,
});

export type AiSendFailure = {
  kind: "SESSION_UNAVAILABLE" | "SEND_FAILED" | "RESULT_UNAVAILABLE";
  question: string;
  requestedChatId?: string;
  sessionId?: string;
};

const initialState: {
  initialized: boolean;
  initializationLoading: boolean;
  initializationError: boolean;
  companiesSettings?: Record<OFFICE_TYPES, AdminPanelCompanySettingsModel>;
  selectedCompanyIds: Array<number>;
  selectedCurrencyId?: number;
  aiModalOpen: boolean;
  aiChatsLoading: boolean;
  aiMessagesLoading: boolean;
  aiSending: boolean;
  aiSendFailure?: AiSendFailure;
  aiPinningChatIds: Array<string>;
  aiDeletingChatIds: Array<string>;
  aiActiveChatId?: string;
  aiFeedbackMessageIds: Array<string>;
  aiChats: Array<AiChatListItemModel>;
  aiMessages: Array<AiChatMessageModel>;
  aiContextOptionsLoading: boolean;
  aiContextOptions?: AgentConversationContextOptionsDto;
} = {
  initialized: false,
  initializationLoading: false,
  initializationError: false,
  companiesSettings: undefined,
  selectedCompanyIds: [],
  selectedCurrencyId: undefined,
  aiModalOpen: false,
  aiChatsLoading: false,
  aiMessagesLoading: false,
  aiSending: false,
  aiPinningChatIds: [],
  aiDeletingChatIds: [],
  aiActiveChatId: undefined,
  aiFeedbackMessageIds: [],
  aiChats: [],
  aiMessages: [],
  aiContextOptionsLoading: false,
  aiContextOptions: undefined,
};

const adminPanelSlice = createSlice({
  name: "adminPanel",
  initialState,
  reducers: {
    restoreHeaderFilters(state, action: PayloadAction<{ companyIds: Array<number>; currencyId: number }>) {
      state.selectedCompanyIds = action.payload.companyIds;
      state.selectedCurrencyId = action.payload.currencyId;
    },
    initialize(state, action: PayloadAction<{ companyIds: Array<number>; currencyId?: number }>) {
      state.initialized = true;
      state.initializationLoading = true;
      state.initializationError = false;
      state.selectedCompanyIds = action.payload.companyIds;
      state.selectedCurrencyId = action.payload.currencyId;
    },
    initializeSuccess(state, action: PayloadAction<Record<OFFICE_TYPES, AdminPanelCompanySettingsModel>>) {
      state.initializationLoading = false;
      state.initializationError = false;
      state.companiesSettings = action.payload;
    },
    initializeFailure(state) {
      state.initializationLoading = false;
      state.initializationError = true;
    },
    retryInitialization(state) {
      state.initializationLoading = true;
      state.initializationError = false;
    },
    changeSelectedCompanyIds(state, action: PayloadAction<Array<number>>) {
      state.selectedCompanyIds = action.payload;
    },
    changeSelectedCurrency(state, action: PayloadAction<number>) {
      state.selectedCurrencyId = action.payload;
    },
    openAiModal(state) {
      state.aiModalOpen = true;
    },
    closeAiModal(state) {
      state.aiModalOpen = false;
    },
    loadAiChats(state) {
      state.aiChatsLoading = true;
    },
    loadAiContextOptions(state) {
      state.aiContextOptionsLoading = true;
    },
    loadAiContextOptionsSuccess(state, action: PayloadAction<AgentConversationContextOptionsDto>) {
      state.aiContextOptions = action.payload;
      state.aiContextOptionsLoading = false;
    },
    loadAiContextOptionsFailure(state) {
      state.aiContextOptionsLoading = false;
    },
    loadAiChatsSuccess(state, action: PayloadAction<Array<AiChatListItemModel>>) {
      state.aiChats = sortAiChats(action.payload);
      state.aiChatsLoading = false;
    },
    loadAiChatsFailure(state) {
      state.aiChatsLoading = false;
    },
    loadAiChatMessages(state, action: PayloadAction<string>) {
      state.aiSendFailure = undefined;
      state.aiActiveChatId = action.payload;
      state.aiMessages = [];
      state.aiMessagesLoading = true;
    },
    loadAiChatMessagesSuccess(state, action: PayloadAction<Array<AiChatMessageModel>>) {
      state.aiMessages = action.payload;
      state.aiMessagesLoading = false;
    },
    loadAiChatMessagesFailure(state) {
      state.aiMessagesLoading = false;
    },
    createAiChat(state) {
      state.aiSendFailure = undefined;
      state.aiActiveChatId = undefined;
      state.aiMessages = [];
      state.aiMessagesLoading = false;
    },
    aiAskQuestion(
      state,
      action: PayloadAction<{
        question: string;
        chatId?: string;
        followUpToken?: string;
        interactionType?: "NEW" | "RETRY" | "CORRECTION";
        selectedCompanyIds?: Array<number>;
        sourceMessageId?: string;
        context?: AgentConversationContextRequestDto;
      }>,
    ) {
      state.aiSending = true;
      state.aiSendFailure = undefined;
      if (action.payload.followUpToken) {
        state.aiMessages.forEach((message) => {
          const selected = message.suggestedActions?.find((item) => item.token === action.payload.followUpToken);
          if (selected && message.suggestedActions) {
            message.suggestedActions.forEach((item) => {
              item.selected = item === selected;
              item.token = undefined;
            });
          }
        });
      }
      if (state.aiActiveChatId === action.payload.chatId) {
        state.aiMessages.push(createLocalUserMessage(action.payload.question));
      }
    },
    aiAskQuestionSuccess(
      state,
      action: PayloadAction<{
        answer: AiAnswerModel;
        question: string;
        requestedChatId?: string;
      }>,
    ) {
      const { answer, question, requestedChatId } = action.payload;
      const currentDialogIsRequestDialog = requestedChatId ? state.aiActiveChatId === requestedChatId : state.aiActiveChatId === undefined;
      const now = dayjs();
      const chat = state.aiChats.find((x) => x.chatId === answer.chatId);

      if (chat) {
        chat.lastMessagePreview = answer.answer;
        chat.lastMessageRole = "ASSISTANT";
        chat.lastFinalStatus = answer.finalStatus;
        chat.updatedAt = now;
        chat.lastMessageAt = now;
      } else {
        state.aiChats.unshift({
          chatId: answer.chatId,
          title: question.slice(0, 80),
          archived: false,
          pinned: false,
          lastMessagePreview: answer.answer,
          lastMessageRole: "ASSISTANT",
          lastFinalStatus: answer.finalStatus,
          lastMessageAt: now,
          updatedAt: now,
          createdAt: now,
        });
      }

      sortAiChats(state.aiChats);

      if (currentDialogIsRequestDialog) {
        state.aiActiveChatId = answer.chatId;
        state.aiMessages.push(createLocalAssistantMessage(answer));
      }

      state.aiSending = false;
    },
    aiAskQuestionFailure(state, action: PayloadAction<AiSendFailure>) {
      state.aiSending = false;
      const failure = action.payload;
      if (state.aiActiveChatId !== failure.requestedChatId) return;
      state.aiSendFailure = failure;
      if (failure.kind !== "RESULT_UNAVAILABLE") {
        const last = state.aiMessages[state.aiMessages.length - 1];
        if (last?.role === "USER" && typeof last.id === "number" && last.id < 0 && last.text === failure.question) {
          state.aiMessages.pop();
        }
      }
      if (failure.sessionId) state.aiActiveChatId = failure.sessionId;
    },
    pinAiChat(state, action: PayloadAction<string>) {
      state.aiPinningChatIds.push(action.payload);
    },
    unpinAiChat(state, action: PayloadAction<string>) {
      state.aiPinningChatIds.push(action.payload);
    },
    updateAiChatPinSuccess(state, action: PayloadAction<AiChatMutationDto>) {
      const chat = state.aiChats.find((x) => x.chatId === action.payload.chatId);
      if (chat) {
        chat.pinned = action.payload.pinned;
        sortAiChats(state.aiChats);
      }
      state.aiPinningChatIds = state.aiPinningChatIds.filter((x) => x !== action.payload.chatId);
    },
    updateAiChatPinFailure(state, action: PayloadAction<string>) {
      state.aiPinningChatIds = state.aiPinningChatIds.filter((x) => x !== action.payload);
    },
    deleteAiChat(state, action: PayloadAction<string>) {
      state.aiDeletingChatIds.push(action.payload);
    },
    deleteAiChatSuccess(state, action: PayloadAction<string>) {
      state.aiChats = state.aiChats.filter((x) => x.chatId !== action.payload);
      state.aiDeletingChatIds = state.aiDeletingChatIds.filter((x) => x !== action.payload);

      if (state.aiActiveChatId === action.payload) {
        state.aiActiveChatId = undefined;
        state.aiMessages = [];
        state.aiMessagesLoading = false;
      }
    },
    deleteAiChatFailure(state, action: PayloadAction<string>) {
      state.aiDeletingChatIds = state.aiDeletingChatIds.filter((x) => x !== action.payload);
    },
    resetChat(state) {
      state.aiActiveChatId = undefined;
      state.aiMessages = [];
    },
    submitAiFeedback(
      state,
      action: PayloadAction<{
        messageId: string;
        rating: "LIKE" | "DISLIKE";
        reason?: string;
        comment?: string;
      }>,
    ) {
      state.aiFeedbackMessageIds.push(action.payload.messageId);
    },
    submitAiFeedbackSuccess(state, action: PayloadAction<{ messageId: string; rating: "LIKE" | "DISLIKE" }>) {
      const message = state.aiMessages.find((item) => String(item.id) === action.payload.messageId);
      if (message) message.feedbackRating = action.payload.rating;
      state.aiFeedbackMessageIds = state.aiFeedbackMessageIds.filter((id) => id !== action.payload.messageId);
    },
    submitAiFeedbackFailure(state, action: PayloadAction<string>) {
      state.aiFeedbackMessageIds = state.aiFeedbackMessageIds.filter((id) => id !== action.payload);
    },
  },
});

export default adminPanelSlice;
